<template>
  <v-container fluid class="pa-0">
    <!-- Header del Lote Actual -->
    <v-card outlined class="mb-4">
      <v-card-text>
        <div class="d-flex align-center">
          <div>
            <h3 class="text-h6 mb-1">{{ batch.name }}</h3>
            <p class="text-caption grey--text mb-0">{{ batch.description }}</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="closeBatch"
          >
            <v-icon left>mdi-check</v-icon>
            Cerrar Lote
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <v-row>
      <!-- Panel de Escaneo -->
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-title>
            <v-icon left>mdi-barcode-scan</v-icon>
            Escanear Producto
          </v-card-title>
          
          <v-card-text>
            <!-- Switch para registro automático y último escaneado -->
            <div class="d-flex align-center mb-4">
              <v-switch
                v-model="autoAddEnabled"
                label="Escaneo rápido (cantidad 1)"
                color="primary"
                dense
                hide-details
                class="mt-0"
              ></v-switch>
              <v-spacer></v-spacer>
              <v-tooltip bottom v-if="lastScannedItem">
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    color="success"
                    outlined
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="editLastScanned"
                    class="cursor-pointer"
                  >
                    <v-icon left small>mdi-pencil</v-icon>
                    Último: {{ lastScannedItem.productName }} ({{ lastScannedItem.countedQuantity }})
                  </v-chip>
                </template>
                <span>Clic para editar el último registro</span>
              </v-tooltip>
            </div>
            
            <!-- Input de código de barras -->
            <v-text-field
              v-model="barcode"
              label="Código de barras / EAN"
              placeholder="Escanea o ingresa el código"
              outlined
              dense
              autofocus
              append-icon="mdi-barcode"
              @keyup.enter="processBarcode"
              ref="barcodeInput"
            >
              <template v-slot:append-outer>
                <v-btn
                  color="primary"
                  @click="processBarcode"
                  :disabled="!barcode"
                >
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </template>
            </v-text-field>

            <!-- Información del producto encontrado -->
            <v-expand-transition>
              <v-alert
                v-if="currentProduct"
                :type="currentProduct.found ? 'success' : 'warning'"
                dense
                class="mb-4"
              >
                  <div v-if="currentProduct.found">
                    <strong>{{ currentProduct.nombre }}</strong>
                    <div class="text-caption">
                      Ref: {{ currentProduct.refe }} | 
                      Stock sistema: {{ Math.round(currentProduct.cantidad) }}
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <v-row dense class="text-caption">
                      <v-col cols="6">
                        <strong>Costo:</strong> 
                        <span class="primary--text font-weight-bold">
                          ${{ formatPrice(currentProduct.costo || 0) }}
                        </span>
                      </v-col>
                      <v-col cols="6">
                        <strong>Precio:</strong> 
                        <span class="success--text font-weight-bold">
                          ${{ formatPrice(currentProduct.precio || 0) }}
                        </span>
                      </v-col>
                    </v-row>
                  </div>
                <div v-else>
                  Producto no encontrado en el inventario
                </div>
              </v-alert>
            </v-expand-transition>
            
            <!-- Notificación de registro automático -->
            <v-expand-transition>
              <v-alert
                v-if="autoAddEnabled && lastScannedItem && showSuccessNotification"
                color="success"
                dark
                dense
                prominent
                class="mb-4"
              >
                <v-row align="center" no-gutters>
                  <v-col class="grow">
                    <div class="d-flex align-center">
                      <v-icon left>mdi-check-circle</v-icon>
                      <div class="flex-grow-1">
                        <div class="font-weight-medium">✓ Escaneado: {{ lastScannedItem.productName }}</div>
                        <div class="text-caption">
                          Cantidad: {{ lastScannedItem.countedQuantity }} | 
                          Costo: ${{ formatPrice(lastScannedItem.productCost || 0) }} | 
                          Precio: ${{ formatPrice(lastScannedItem.productPrice || 0) }}
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-col class="shrink">
                    <v-btn
                      text
                      small
                      @click="editLastScanned"
                    >
                      Editar cantidad
                    </v-btn>
                  </v-col>
                </v-row>
              </v-alert>
            </v-expand-transition>

            <!-- Input de cantidad (solo visible si no es automático) -->
            <v-expand-transition>
              <v-text-field
                v-if="!autoAddEnabled"
                v-model.number="quantity"
                label="Cantidad contada"
                type="number"
                outlined
                dense
                @keyup.enter="addCount"
                ref="quantityInput"
                class="mt-3"
              >
                <template v-slot:append-outer>
                  <v-btn
                    color="success"
                    @click="addCount"
                    :disabled="!currentProduct || !currentProduct.found || !quantity"
                  >
                    <v-icon>mdi-plus</v-icon>
                    Agregar
                  </v-btn>
                </template>
              </v-text-field>
            </v-expand-transition>

            <!-- Búsqueda manual -->
            <v-expansion-panels flat>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span>
                    <v-icon left>mdi-magnify</v-icon>
                    Búsqueda Manual
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field
                    v-model="searchQuery"
                    label="Buscar por nombre o referencia"
                    outlined
                    dense
                    @input="searchProducts"
                  ></v-text-field>
                  
                  <v-list dense max-height="200" class="overflow-y-auto">
                    <v-list-item
                      v-for="product in searchResults"
                      :key="product.ean"
                      @click="selectProduct(product)"
                    >
                      <v-list-item-content>
                        <v-list-item-title>{{ product.nombre }}</v-list-item-title>
                        <v-list-item-subtitle>
                          EAN: {{ product.ean }} | Ref: {{ product.refe }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            
            <!-- Ayuda contextual -->
            <div class="text-caption grey--text mt-4">
              <v-icon small>mdi-information</v-icon>
              <span v-if="autoAddEnabled">
                Modo rápido activado: Escanea para agregar automáticamente con cantidad 1
              </span>
              <span v-else>
                Modo manual: Escanea e ingresa la cantidad antes de agregar
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Lista de Items Escaneados -->
      <v-col cols="12" md="6">
        <v-card outlined>
          <v-card-title>
            <v-icon left>mdi-format-list-bulleted</v-icon>
            Items Escaneados
            <v-spacer></v-spacer>
            <v-chip color="primary" small>
              {{ scannedItems.length }}
            </v-chip>
            <v-btn
              icon
              small
              @click="exportBatch"
              :disabled="scannedItems.length === 0"
              class="ml-2"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </v-card-title>
          
          <v-card-text class="pa-0">
            <v-data-table
              :headers="headers"
              :items="scannedItems"
              :items-per-page="10"
              dense
              class="elevation-0"
            >
              <template v-slot:item.productName="{ item }">
                <div>
                  <div>{{ item.productName }}</div>
                  <div class="text-caption grey--text">
                    <span class="primary--text">Costo: ${{ formatPrice(item.productCost || 0) }}</span> | 
                    <span class="success--text">Precio: ${{ formatPrice(item.productPrice || 0) }}</span>
                  </div>
                </div>
              </template>
              
              <template v-slot:item.systemQuantity="{ item }">
                {{ item.systemQuantity }}
              </template>
              
              <template v-slot:item.countedQuantity="{ item }">
                {{ item.countedQuantity }}
              </template>
              
              <template v-slot:item.lastUpdate="{ item }">
                {{ formatTime(item.scannedAt || item.lastUpdate) }}
              </template>
              
              <template v-slot:item.difference="{ item }">
                <v-chip
                  x-small
                  :color="getDifferenceColor(item.systemQuantity, item.countedQuantity)"
                >
                  {{ item.countedQuantity - item.systemQuantity }}
                </v-chip>
              </template>
              
              <template v-slot:item.actions="{ item }">
                <v-btn
                  icon
                  x-small
                  @click="editScan(item)"
                >
                  <v-icon x-small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  @click="deleteScan(item)"
                >
                  <v-icon x-small color="error">mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Resumen del Lote -->
        <v-card outlined class="mt-4">
          <v-card-text>
            <h4 class="text-subtitle-1 mb-2">Resumen del Lote</h4>
            <v-row dense>
              <v-col cols="6">
                <div class="text-caption grey--text">Productos únicos</div>
                <div class="text-h6">{{ uniqueProductsCount }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">Total unidades</div>
                <div class="text-h6">{{ totalUnitsCount }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">Con diferencias</div>
                <div class="text-h6 error--text">{{ productsWithDifference }}</div>
              </v-col>
              <v-col cols="6">
                <div class="text-caption grey--text">Exactos</div>
                <div class="text-h6 success--text">{{ productsExact }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo de Edición -->
    <v-dialog v-model="editDialog.show" max-width="400">
      <v-card>
        <v-card-title>Editar Conteo</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="editDialog.quantity"
            label="Cantidad contada"
            type="number"
            outlined
            dense
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="editDialog.show = false">Cancelar</v-btn>
          <v-btn color="primary" @click="saveEdit">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Diálogo de Confirmación -->
    <v-dialog v-model="confirmDialog.show" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="warning">mdi-alert</v-icon>
          {{ confirmDialog.title }}
        </v-card-title>
        <v-card-text class="pt-4">
          {{ confirmDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="confirmDialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            @click="executeConfirmAction"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'AuditScanner',
  
  props: {
    batch: {
      type: Object,
      required: true
    },
    inventory: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      // Estado del escaneo
      barcode: '',
      quantity: 1,
      currentProduct: null,
      autoAddEnabled: true,
      lastScannedItem: null,
      showSuccessNotification: false,
      
      // Búsqueda manual
      searchQuery: '',
      searchResults: [],
      
      // Items escaneados
      scannedItems: [],
      
      // Headers de tabla
      headers: [
        { text: 'Producto', value: 'productName', width: '35%' },
        { text: 'Sistema', value: 'systemQuantity', align: 'center' },
        { text: 'Contado', value: 'countedQuantity', align: 'center' },
        { text: 'Diferencia', value: 'difference', align: 'center' },
        { text: 'Última Actualización', value: 'lastUpdate', align: 'center' },
        { text: '', value: 'actions', align: 'center', sortable: false }
      ],
      
      // Diálogo de edición
      editDialog: {
        show: false,
        item: null,
        quantity: 0
      },
      
      // Diálogo de confirmación
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        action: null
      }
    };
  },

  computed: {
    uniqueProductsCount() {
      return this.scannedItems.length;
    },

    totalUnitsCount() {
      return this.scannedItems.reduce((sum, item) => sum + (item.countedQuantity || 0), 0);
    },

    productsWithDifference() {
      return this.scannedItems.filter(item => 
        item.countedQuantity !== item.systemQuantity
      ).length;
    },

    productsExact() {
      return this.scannedItems.filter(item => 
        item.countedQuantity === item.systemQuantity
      ).length;
    }
  },

  mounted() {
    // Focus en el input de código de barras
    this.$nextTick(() => {
      if (this.$refs.barcodeInput) {
        this.$refs.barcodeInput.focus();
      }
    });
  },

  watch: {
    'batch.audits': {
      handler(newAudits) {
        if (newAudits && Array.isArray(newAudits)) {
          this.scannedItems = newAudits.map(audit => ({
            ...audit,
            id: audit.id || `${audit.batchId}-${audit.productId}`,
            scannedAt: audit.lastUpdate || audit.scannedAt
          }));
        }
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    processBarcode() {
      if (!this.barcode) return;
      
      const product = this.inventory.find(item => 
        item.ean === this.barcode || 
        item.refe === this.barcode
      );
      
      if (product) {
        this.currentProduct = { ...product, found: true };
        
        if (this.autoAddEnabled) {
          this.quantity = 1;
          this.addCount();
        } else {
          this.$nextTick(() => {
            if (this.$refs.quantityInput) {
              this.$refs.quantityInput.focus();
            }
          });
        }
      } else {
        this.currentProduct = { found: false };
      }
    },

    addCount() {
      if (!this.currentProduct || !this.currentProduct.found || (!this.quantity && !this.autoAddEnabled)) return;
      
      const finalQuantity = this.autoAddEnabled ? 1 : this.quantity;
      
      const existingIndex = this.scannedItems.findIndex(
        item => item.productId === this.currentProduct.ean
      );
      
      if (existingIndex > -1) {
        const existingItem = this.scannedItems[existingIndex];
        const updatedItem = {
          ...existingItem,
          countedQuantity: existingItem.countedQuantity + finalQuantity,
          scannedAt: new Date(),
          lastUpdate: new Date(),
          productCost: this.currentProduct.costo || existingItem.productCost || 0,
          productPrice: this.currentProduct.precio || existingItem.productPrice || 0,
          quantityToAdd: finalQuantity
        };
        
        this.scannedItems[existingIndex] = updatedItem;
        this.lastScannedItem = updatedItem;
        
        this.$emit('item-scanned', updatedItem);
      } else {
        const scanData = {
          id: `${this.batch.id}-${this.currentProduct.ean}`,
          batchId: this.batch.id,
          productId: this.currentProduct.ean,
          productName: this.currentProduct.nombre,
          productRef: this.currentProduct.refe,
          productCost: this.currentProduct.costo || 0,
          productPrice: this.currentProduct.precio || 0,
          systemQuantity: Math.round(this.currentProduct.cantidad) || 0,
          countedQuantity: finalQuantity,
          quantityToAdd: finalQuantity,
          scannedAt: new Date(),
          lastUpdate: new Date(),
          userId: window.userId || 'user'
        };
        
        this.scannedItems.push(scanData);
        this.lastScannedItem = scanData;
        
        this.$emit('item-scanned', scanData);
      }
      
      this.showSuccessNotification = true;
      setTimeout(() => {
        this.showSuccessNotification = false;
      }, 3000);
      
      this.barcode = '';
      this.quantity = 1;
      this.currentProduct = null;
      
      this.$nextTick(() => {
        if (this.$refs.barcodeInput) {
          this.$refs.barcodeInput.focus();
        }
      });
    },

    searchProducts() {
      if (!this.searchQuery) {
        this.searchResults = [];
        return;
      }
      
      const query = this.searchQuery.toLowerCase();
      this.searchResults = this.inventory.filter(item =>
        item.nombre.toLowerCase().includes(query) ||
        item.refe.toLowerCase().includes(query) ||
        item.ean.includes(query)
      ).slice(0, 10);
    },

    selectProduct(product) {
      this.barcode = product.ean;
      this.processBarcode();
      this.searchQuery = '';
      this.searchResults = [];
    },

    editLastScanned() {
      if (this.lastScannedItem) {
        this.editScan(this.lastScannedItem);
      }
    },

    editScan(item) {
      this.editDialog = {
        show: true,
        item: item,
        quantity: item.countedQuantity
      };
    },

    saveEdit() {
      if (this.editDialog.item) {
        this.editDialog.item.countedQuantity = this.editDialog.quantity;
        this.editDialog.item.scannedAt = new Date();
        this.editDialog.item.lastUpdate = new Date();
        
        this.$emit('item-scanned', {
          ...this.editDialog.item,
          countedQuantity: this.editDialog.quantity,
          isEdit: true
        });
        
        if (this.lastScannedItem && this.lastScannedItem.id === this.editDialog.item.id) {
          this.lastScannedItem = this.editDialog.item;
        }
      }
      this.editDialog.show = false;
    },

    deleteScan(item) {
      this.showConfirm(
        'Eliminar Conteo',
        `¿Está seguro de eliminar este conteo de ${item.productName}?`,
        () => {
          const index = this.scannedItems.findIndex(scan => scan.id === item.id);
          if (index > -1) {
            this.scannedItems.splice(index, 1);
            
            this.$emit('item-scanned', { 
              batchId: item.batchId,
              productId: item.productId,
              deleted: true 
            });
            
            if (this.lastScannedItem && this.lastScannedItem.id === item.id) {
              this.lastScannedItem = null;
            }
          }
        }
      );
    },

    closeBatch() {
      if (this.scannedItems.length === 0) {
        this.showConfirm(
          'Lote Vacío',
          'No hay items escaneados en este lote. ¿Desea cerrarlo de todas formas?',
          () => {
            this.$emit('batch-closed', this.batch.id);
          }
        );
        return;
      }
      
      this.showConfirm(
        'Cerrar Lote',
        `¿Está seguro de cerrar el lote "${this.batch.name}" con ${this.scannedItems.length} items escaneados?`,
        () => {
          this.$emit('batch-closed', this.batch.id);
        }
      );
    },

    executeConfirmAction() {
      if (this.confirmDialog.action) {
        this.confirmDialog.action();
      }
      this.confirmDialog.show = false;
    },

    showConfirm(title, message, action) {
      this.confirmDialog = {
        show: true,
        title,
        message,
        action
      };
    },

    exportBatch() {
      const data = this.scannedItems.map(item => ({
        Producto: item.productName,
        Referencia: item.productRef,
        Costo: '$' + this.formatPrice(item.productCost || 0),
        Precio: '$' + this.formatPrice(item.productPrice || 0),
        'Stock Sistema': item.systemQuantity,
        'Stock Contado': item.countedQuantity,
        Diferencia: item.countedQuantity - item.systemQuantity,
        'Última Actualización': this.formatTime(item.scannedAt || item.lastUpdate)
      }));
      
      const csv = this.convertToCSV(data);
      
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `auditoria_${this.batch.name.replace(/\s+/g, '_')}_${Date.now()}.csv`;
      link.click();
    },

    convertToCSV(data) {
      if (!data.length) return '';
      
      const headers = Object.keys(data[0]);
      const csv = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => 
            JSON.stringify((row[header] || '').toString())
          ).join(',')
        )
      ].join('\r\n');
      
      return csv;
    },

    getDifferenceColor(system, counted) {
      const diff = counted - system;
      if (diff === 0) return 'success';
      if (diff > 0) return 'warning';
      return 'error';
    },

    formatTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    formatPrice(value) {
      if (!value && value !== 0) return '0';
      return new Intl.NumberFormat('es-CO', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Math.round(value));
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}
</style>
